import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { invalidateUserXpRead } from "../utils/userXpReader";
import { subscribeProgressSaved } from "../utils/progressEvents";
import { readDeviceXp, readWorkspaceXp } from "../utils/deviceXp";
import { subscribeDeviceProgress } from "../utils/deviceProgress";
import { useOnline } from "./useOnline";
import { computeXpBreakdown, ProgressSnapshots, xpProgress } from "../utils/xp";

const features = computeXpBreakdown({}).entries.map(({ feature }) => feature);
const NO_SNAPSHOTS: ProgressSnapshots = {};

export function useUserXp() {
    const online = useOnline();
    const { status, user } = useAuth();
    const identity = status === "authenticated" ? user?.identityId : undefined;
    const [attempt, setAttempt] = useState(0);
    useEffect(() => subscribeProgressSaved((change) => {
        if (identity === change.userId && features.some((feature) => feature === change.feature)) {
            setAttempt((value) => value + 1);
        }
    }), [identity]);
    const [state, setState] = useState<{
        identity?: string; status: "loading" | "ready" | "error";
        snapshots: ProgressSnapshots; hasResolved: boolean; source?: "device" | "cloud"; partial?: boolean;
    }>({ status: "loading", snapshots: {}, hasResolved: false });

    useEffect(() => {
        if (!identity) return;
        let cancelled = false;
        // Retain this account's last confirmed progress while a refresh is in
        // flight. The level UI can then show a small, local spinner instead
        // of looking like the whole header/page has been reset.
        setState((current) => current.identity === identity
            ? { ...current, status: "loading" }
            : { identity, status: "loading", snapshots: {}, hasResolved: false });
        readWorkspaceXp(identity, online, () => !cancelled).then((workspace) => {
            if (cancelled) return;
            setState({ identity, status: "ready", snapshots: workspace.snapshots, source: workspace.source, partial: workspace.partial, hasResolved: true });
        }).catch(() => {
            if (!cancelled) setState((current) => current.identity === identity
                ? { ...current, status: "error" }
                : { identity, status: "error", snapshots: {}, hasResolved: false });
        });
        return () => { cancelled = true; };
    }, [identity, attempt, online]);

    useEffect(() => {
        if (!identity) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        const unsubscribe = subscribeDeviceProgress((owner, feature) => {
            if (owner !== identity || !features.some(key => key === feature)) return;
            invalidateUserXpRead(identity);
            clearTimeout(timer);
            timer = setTimeout(() => {
                void readDeviceXp(identity).then(local => {
                    if (!cancelled && local.available) setState({ identity, status: "ready", snapshots: local.snapshots,
                        source: local.pending || local.partial || !online ? "device" : "cloud", partial: local.partial, hasResolved: true });
                }).catch(() => { /* the main reader reports storage errors */ });
            }, 0);
        });
        return () => { cancelled = true; clearTimeout(timer); unsubscribe(); };
    }, [identity, online]);

    // Never expose the previous account's data, even on the render before effect cleanup.
    const visible = identity && state.identity === identity;
    const snapshots = visible ? state.snapshots : NO_SNAPSHOTS;
    const breakdown = computeXpBreakdown(snapshots);
    return {
        // The raw per-feature rows the breakdown was computed from, for
        // consumers (mission achievements) that need which items are done,
        // not just how many.
        source: visible ? state.source : undefined, partial: visible ? state.partial : false,
        snapshots,
        breakdown, progress: xpProgress(breakdown.total),
        status: !identity ? "signed-out" : visible ? state.status : "loading",
        hasProgress: visible && state.hasResolved,
        refresh: () => {
            if (identity) invalidateUserXpRead(identity);
            setAttempt((value) => value + 1);
        },
    };
}
